import type { ReactNode } from 'react';
import type { AuthStatusResponse } from '../../types';
import { Button, cx } from '../ui/primitives';

/**
 * Page chrome: the title bar with the Google connection state, an optional
 * step indicator, and the content column.
 */

export type Step =
  | 'dashboard'
  | 'requests'
  | 'input'
  | 'review'
  | 'destination'
  | 'processing'
  | 'success';

/**
 * The steps of *creating* one request.
 *
 * `requests` is deliberately absent: it is the home screen, not a stage of the
 * flow, and numbering it would imply the operator has to pass through it on the
 * way to something.
 */
const STEP_ORDER: Step[] = ['input', 'review', 'destination', 'processing', 'success'];

const STEP_LABELS: Record<Step, string> = {
  dashboard: 'Home',
  requests: 'Requests',
  // The start page combines sign-in with the brief paste area.
  input: 'Start',
  review: 'Review & Edit',
  destination: 'Destination',
  processing: 'Processing',
  success: 'Done',
};


/** The two places the application has: the dashboard, and the work. */
const SECTIONS = [
  { key: 'dashboard' as const, label: 'Home' },
  { key: 'requests' as const, label: 'Requests' },
];

/**
 * Top-level navigation.
 *
 * Two tabs rather than a sidebar: there are two destinations, and a sidebar for
 * two entries spends horizontal space on furniture. Every step of creating a
 * request belongs under Requests, so the tab stays lit while the operator works
 * through the flow — the flow is a journey inside that section, not a third place.
 */
function SectionTabs({
  step,
  onNavigate,
}: {
  step: Step;
  onNavigate: (section: 'dashboard' | 'requests') => void;
}) {
  const current = step === 'dashboard' ? 'dashboard' : 'requests';

  return (
    <nav aria-label="Sections" className="flex gap-1">
      {SECTIONS.map((section) => {
        const active = section.key === current;

        return (
          <button
            key={section.key}
            type="button"
            aria-current={active ? 'page' : undefined}
            onClick={() => onNavigate(section.key)}
            className={cx(
              'rounded-input px-3.5 py-2 text-field font-medium transition-colors',
              'focus-visible:ring-2 focus-visible:ring-accent/40',
              active
                ? 'bg-accent-soft text-accent-hover'
                : 'text-ink-muted hover:bg-surface-muted hover:text-ink',
            )}
          >
            {section.label}
          </button>
        );
      })}
    </nav>
  );
}

function Stepper({ current }: { current: Step }) {
  const currentIndex = STEP_ORDER.indexOf(current);

  // Nothing to show on the home screen, which is outside the flow.
  if (currentIndex < 0) return null;

  return (
    /*
     * One scrolling row on a phone rather than a wrapping block. Five steps
     * wrapped onto three lines pushed the content down the page and broke the
     * left-to-right sequence that makes a stepper readable at all.
     */
    <ol className="-mx-1 flex items-center gap-x-2 overflow-x-auto px-1 pb-1 sm:mx-0 sm:flex-wrap sm:gap-y-1 sm:overflow-visible sm:px-0 sm:pb-0">
      {STEP_ORDER.map((step, index) => {
        const isDone = index < currentIndex;
        const isCurrent = index === currentIndex;

        return (
          <li key={step} className="flex shrink-0 items-center gap-2">
            <span
              className={cx(
                'flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-semibold',
                isCurrent && 'bg-accent text-white',
                isDone && 'bg-accent-soft text-accent',
                !isCurrent && !isDone && 'bg-surface-muted text-ink-faint',
              )}
              aria-hidden="true"
            >
              {isDone ? '✓' : index + 1}
            </span>
            <span
              className={cx(
                'whitespace-nowrap text-micro',
                isCurrent ? 'font-medium text-ink' : 'text-ink-faint',
              )}
              aria-current={isCurrent ? 'step' : undefined}
            >
              {STEP_LABELS[step]}
            </span>
            {index < STEP_ORDER.length - 1 ? (
              <span aria-hidden="true" className="mx-1 h-px w-4 bg-line" />
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}

function ConnectionStatus({
  auth,
  onConnect,
  onDisconnect,
}: {
  auth: AuthStatusResponse | null;
  onConnect: () => void;
  onDisconnect: () => void;
}) {
  if (!auth?.connected) {
    return (
      <Button variant="secondary" onClick={onConnect} className="px-4 py-2">
        Connect Google Drive
      </Button>
    );
  }

  return (
    <div className="flex items-center gap-3">
      <span className="flex items-center gap-2 text-micro text-ink-muted">
        <span
          aria-hidden="true"
          className={cx(
            'h-2 w-2 rounded-full',
            auth.authorized ? 'bg-success' : 'bg-danger',
          )}
        />
        {auth.authorized ? 'Google Drive Connected' : 'Account not permitted'}
        <span className="hidden text-ink-faint sm:inline">&middot; {auth.email}</span>
      </span>
      <button
        type="button"
        onClick={onDisconnect}
        className="text-micro text-ink-faint underline-offset-2 hover:text-ink hover:underline"
      >
        Sign out
      </button>
    </div>
  );
}

export function AppShell({
  title,
  step,
  auth,
  onConnect,
  onDisconnect,
  onNavigate,
  children,
}: {
  title: string;
  /** Omitted on the login screen, where there is no flow to indicate. */
  step?: Step;
  auth: AuthStatusResponse | null;
  onConnect: () => void;
  onDisconnect: () => void;
  /** Omitted before sign-in, when there is nowhere to navigate to. */
  onNavigate?: (section: 'dashboard' | 'requests') => void;
  children: ReactNode;
}) {
  return (
    <div className="min-h-screen bg-canvas">
      <header className="border-b border-line bg-surface">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3 px-5 py-4 sm:px-8">
          <h1 className="text-heading font-semibold text-ink">{title}</h1>
          <ConnectionStatus auth={auth} onConnect={onConnect} onDisconnect={onDisconnect} />
        </div>
      </header>

      {step && onNavigate ? (
        <div className="mx-auto max-w-5xl px-5 pt-4 sm:px-8">
          <SectionTabs step={step} onNavigate={onNavigate} />
        </div>
      ) : null}

      {step ? (
        <div className="mx-auto max-w-5xl px-5 pt-4 sm:px-8">
          <Stepper current={step} />
        </div>
      ) : null}

      <main className="mx-auto max-w-5xl px-5 py-6 sm:px-8 sm:py-8">{children}</main>
    </div>
  );
}
